'use client';

import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';

import { Flex } from '@chakra-ui/react';

import { Loader } from 'ui';

// Dynamically loading ReactPlayer
const ReactPlayer = dynamic(() => import('react-player/lazy'), {
  ssr: false,
  loading: () => (
    <div
      style={{
        backgroundColor: '#000',
        width: '100%',
        height: '100%',
        position: 'absolute'
      }}
    />
  )
});

type VideoPlayerProps = {
  url: string;
};

const VideoPlayer = ({ url }: VideoPlayerProps) => {
  const [playing, setPlaying] = useState(false);
  const [shouldLoadPlayer, setShouldLoadPlayer] = useState(false);

  const isTikTok = /^https?:\/\/(?:www\.)?tiktok\.com\/@[^/]+\/video\/\d+/.test(
    url
  );
  const videoId = isTikTok ? url.match(/\/video\/(\d+)/)?.[1] : url;

  // Load the TikTok script every time the TikTok video modal is opened
  useEffect(() => {
    if (isTikTok) {
      const src = 'https://www.tiktok.com/embed.js';
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      document.body.appendChild(script);
    }
  }, [isTikTok, videoId]);

  const handlePlay = () => {
    if (!shouldLoadPlayer) {
      setShouldLoadPlayer(true);
    }
    setPlaying(true);
  };

  const handlePause = () => {
    setPlaying(false);
  };

  return (
    <Flex w='full' h='100%' flex={1}>
      {isTikTok ? (
        // Display TikTok-embed
        <blockquote
          className='tiktok-embed'
          cite={url}
          data-video-id={videoId}
          style={{
            overflow: 'auto',
            display: 'flex',
            alignItems: 'center',
            margin: 0
          }}
        >
          <Flex justifyContent='center' alignItems='center' w='full' h='full'>
            <Loader absolute />
          </Flex>
        </blockquote>
      ) : (
        // Render ReactPlayer; key forces it to remount on every open
        shouldLoadPlayer && (
          <ReactPlayer
            key={url}
            url={url}
            playing={playing}
            controls={playing}
            width='100%'
            height='100%'
            style={{ position: 'absolute', top: 0, left: 0 }}
            onPlay={handlePlay}
            onPause={handlePause}
            config={{
              file: { attributes: { preload: 'none' } },
              youtube: { playerVars: { preload: 'none' } }
            }}
          />
        )
      )}
    </Flex>
  );
};
export default VideoPlayer;
